import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const pagesEnfants = [
  { title: 'Volet roulant bloqué', desc: 'Lames sorties, axe désaxé, tablier bloqué ? Nous diagnostiquons et débloquons votre volet.', href: '/volet-roulant-bloque-lyon/' },
  { title: 'Volet qui ne remonte plus', desc: 'Moteur HS, condensateur, fin de course, sangle cassée : nous trouvons la panne et réparons.', href: '/volet-roulant-ne-remonte-plus-lyon/' },
  { title: 'Réparation moteur', desc: 'Remplacement de moteur de volet roulant toutes marques : Somfy, Bubendorff, Profalux, Nice…', href: '/reparation-moteur-volet-roulant-lyon/' },
  { title: 'Changement de sangle', desc: 'Sangle usée ou cassée ? Remplacement rapide avec possibilité de passage en motorisation.', href: '/changer-sangle-volet-roulant-lyon/' },
  { title: 'Manivelle et treuil', desc: 'Manivelle bloquée, cardan usé, treuil défectueux : réparation ou remplacement.', href: '/manivelle-volet-roulant-lyon/' },
  { title: 'Remplacement de lames', desc: 'Lames PVC ou aluminium cassées ou abîmées ? Remplacement à l\'unité ou du tablier complet.', href: '/changer-lame-volet-roulant-lyon/' },
  { title: 'Motorisation', desc: 'Passez de votre volet manuel à un volet électrique avec télécommande. Installation rapide.', href: '/motorisation-volet-roulant-lyon/' },
  { title: 'Volet roulant solaire', desc: 'Panne de batterie ou de panneau solaire ? Dépannage et installation de volets solaires.', href: '/volet-roulant-solaire-lyon/' },
  { title: 'Volet roulant électrique', desc: 'Pannes électriques : moteur, condensateur, télécommande, fin de course. Diagnostic et réparation.', href: '/volet-roulant-electrique-lyon/' },
];

const faqData = [
  { q: 'Combien coûte la réparation d\'un volet roulant à Lyon ?', a: 'Le coût dépend de la panne et des pièces à remplacer. Nous établissons un devis gratuit et détaillé avant toute intervention. Une réparation simple (sangle, manivelle) est généralement plus économique qu\'un remplacement de moteur. Contactez-nous pour un diagnostic précis.' },
  { q: 'Intervenez-vous le week-end sur Lyon ?', a: 'Oui, nous intervenons 7 jours sur 7, y compris le samedi et le dimanche, de 7h à 21h. Nous savons qu\'une panne de volet roulant peut être urgente, notamment pour des raisons de sécurité.' },
  { q: 'Quels types de volets roulants réparez-vous ?', a: 'Nous réparons tous les types de volets roulants : manuels (sangle, manivelle), électriques (filaire ou radio), et solaires. Nous intervenons sur les volets PVC comme sur les volets aluminium, en rénovation comme en construction.' },
  { q: 'Combien de temps dure une intervention de réparation de volet roulant ?', a: 'La plupart des interventions courantes (changement de sangle, remplacement de moteur, déblocage) sont réalisées en 1 à 2 heures. Les interventions plus complexes comme le remplacement d\'un tablier complet peuvent prendre une demi-journée.' },
];

const pannesCourantes = [
  'Volet roulant bloqué en position basse ou haute',
  'Volet qui ne remonte plus ou qui ne descend plus',
  'Moteur qui tourne dans le vide ou qui fait un bruit anormal',
  'Sangle de volet roulant cassée ou effilochée',
  'Manivelle dure ou bloquée, cardan usé',
  'Lames de volet roulant cassées, tordues ou désolidarisées',
  'Télécommande qui ne fonctionne plus',
  'Volet roulant solaire qui ne charge plus',
  'Tablier qui sort du rail',
  'Fin de course à régler',
];

export default function VoletRoulantPilier() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openPannes, setOpenPannes] = useState(false);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Réparation et dépannage de volet roulant à Lyon',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', '@id': 'https://depanfenetres-lyon.fr/#business' },
    areaServed: { '@type': 'City', name: 'Lyon' },
  };

  return (
    <>
      <SEOHead
        title="Réparation volet roulant à Lyon | Dépannage 7j/7 | DFPRO"
        description="Volet roulant bloqué, qui ne remonte plus, moteur ou sangle HS ? Réparation à Lyon sous 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Appelez DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/reparation-volet-roulant-lyon/"
        jsonLd={{ ...faqJsonLd, ...serviceJsonLd }}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-pilier-2026.webp" alt="Réparation et dépannage de volet roulant à Lyon par DFPRO Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Réparation volet roulant Lyon</span>
            </nav>

            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">
              Réparation et dépannage de volet roulant à Lyon
            </h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Votre volet roulant est en panne ? Bloqué, moteur HS, sangle cassée ? DFPRO Lyon intervient rapidement dans toute la métropole de Lyon pour diagnostiquer et réparer votre volet roulant. Toutes marques, tous types de volets, 7j/7.
            </p>
            <div className="mt-6">
              <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm px-5 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                Appeler pour un dépannage — 09 80 80 36 84
              </a>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Un service complet de dépannage de volet roulant sur Lyon</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Un volet roulant en panne, c'est à la fois une gêne au quotidien et un risque pour la sécurité de votre habitation. Chez DFPRO Lyon, nous comprenons l'urgence et nous engageons à intervenir sous 24 à 48 heures après votre appel, quel que soit le jour de la semaine.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Nos techniciens sont formés au diagnostic et à la réparation de tous les types de volets roulants : manuels à sangle ou à manivelle, électriques filaires ou radio, et volets solaires. Nous maîtrisons les mécanismes des grandes marques comme Somfy, Bubendorff, Profalux, Nice, et bien d'autres.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Chaque intervention commence par un diagnostic complet et gratuit. Nous vous expliquons clairement l'origine de la panne, les solutions possibles, et nous vous remettons un devis détaillé avant de commencer les travaux. Pas de mauvaise surprise, pas de frais cachés.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Pannes courantes de volet roulant</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Les pannes de volet roulant peuvent avoir de nombreuses causes. Voici les problèmes que nous rencontrons le plus fréquemment lors de nos interventions à Lyon et dans la métropole :
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {pannesCourantes.map((panne, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 w-4 h-4 flex-shrink-0 flex items-center justify-center"></i>
                        <span className="text-sm text-secondary-500">{panne}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Types de volets roulants pris en charge</h2>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="bg-background-100 rounded-lg p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Volet roulant manuel</h3>
                      <p className="mt-2 text-xs text-secondary-500 leading-relaxed">Sangle, manivelle ou treuil : nous réparons tous les systèmes de commande manuelle. Remplacement de sangle, réparation de manivelle, changement de treuil.</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Volet roulant électrique</h3>
                      <p className="mt-2 text-xs text-secondary-500 leading-relaxed">Motorisation filaire ou radio : diagnostic de panne moteur, remplacement de condensateur, réglage de fin de course, programmation de télécommande.</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Volet roulant solaire</h3>
                      <p className="mt-2 text-xs text-secondary-500 leading-relaxed">Système autonome avec panneau solaire et batterie : diagnostic de charge, remplacement de batterie, dépannage du panneau solaire.</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Volet roulant PVC & alu</h3>
                      <p className="mt-2 text-xs text-secondary-500 leading-relaxed">Quel que soit le matériau de votre tablier, nous intervenons sur les volets PVC comme sur les volets aluminium, en pose rénovation ou traditionnelle.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Déroulement d'une intervention</h2>
                  <div className="mt-4 space-y-4">
                    {[
                      { title: '1. Diagnostic sur place', desc: 'Notre technicien se déplace à votre domicile, à Lyon ou dans la métropole, pour examiner votre volet roulant. Il identifie l\'origine exacte de la panne et vérifie l\'état général du mécanisme.' },
                      { title: '2. Devis gratuit et détaillé', desc: 'Nous vous présentons un devis clair, avec le détail des pièces à remplacer et le coût de la main-d\'œuvre. Vous décidez en toute transparence, sans aucun engagement.' },
                      { title: '3. Réparation dans la foulée', desc: 'Dans la majorité des cas, notre technicien dispose des pièces nécessaires dans son véhicule et réalise la réparation immédiatement. Votre volet est remis en état en 1 à 2 heures.' },
                      { title: '4. Garantie 10 ans', desc: 'Toutes nos réparations sont garanties 10 ans, pièces et main-d\'œuvre. C\'est notre engagement qualité pour votre tranquillité.' },
                    ].map((step, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-background-50">{idx + 1}</span>
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-sm text-foreground-800">{step.title}</h3>
                          <p className="mt-1 text-sm text-secondary-500 leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Pannes spécifiques</h3>
                  <nav className="space-y-1">
                    {pagesEnfants.map((page, idx) => (
                      <Link
                        key={idx}
                        to={page.href}
                        className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors"
                      >
                        {page.title}
                      </Link>
                    ))}
                  </nav>
                  <div className="mt-5 pt-5 border-t border-background-200">
                    <a href="tel:+33980803684" className="block w-full text-center bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm px-4 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-phone-fill w-4 h-4 flex items-center justify-center inline mr-1.5"></i>
                      Appeler
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 mb-6">Tous nos services volet roulant à Lyon</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pagesEnfants.map((page, idx) => (
                <Link
                  key={idx}
                  to={page.href}
                  className="block bg-background-50 border border-background-200 rounded-lg p-4 hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <h3 className="font-heading font-semibold text-sm text-foreground-800">{page.title}</h3>
                  <p className="mt-1.5 text-xs text-secondary-500 leading-relaxed">{page.desc}</p>
                  <span className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-500">
                    En savoir plus
                    <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes sur la réparation de volet roulant</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Besoin d'un dépannage de volet roulant à Lyon ?"
          subtitle="Appelez-nous au 09 80 80 36 84. Devis gratuit, intervention rapide, garantie 10 ans."
          buttonText="Appeler le 09 80 80 36 84"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}